import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useWeekBoundary } from '../useWeekBoundary';
import {
  Spinner,
  addDays,
  dayName,
  shortDate,
  startOfWeek,
  todayIso,
} from '../components/ui';
import { visibleModules, type DashboardModuleProps, type ModulePage } from '../modules/registry';
import type { Language, Me, TodayView, WeekView } from '../types';

const TITLES = {
  today: { en: 'Today', fr: "Aujourd'hui" },
  plan: { en: 'Plan the week', fr: 'Planifier la semaine' },
} as const;

const NAV = {
  en: { prev: 'Previous week', next: 'Next week', thisWeek: 'This week', current: 'current week' },
  fr: {
    prev: 'Semaine précédente',
    next: 'Semaine suivante',
    thisWeek: 'Cette semaine',
    current: 'semaine en cours',
  },
} as const;

/**
 * Both household pages, assembled from the module registry (§3, §4).
 *
 * "Today" is what the kitchen needs while standing in it. "Plan the week" is
 * the sit-down job of filling in the days up to the next shop. They share their
 * data — one fetch, one refresh — but deliberately not their page.
 */
export function DashboardPage({
  page,
  language,
  me,
}: {
  page: ModulePage;
  language: Language;
  me: Me;
}) {
  const [today, setToday] = useState<TodayView | null>(null);
  const [week, setWeek] = useState<WeekView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Today always means today. Planning opens on the week actually worth
  // planning, which on the day before the shop is the one ahead rather than
  // the one that is nearly over. Where that week starts is the household's
  // own answer, so it is waited for rather than guessed at.
  const todayDate = todayIso();
  const boundary = useWeekBoundary(todayDate);
  const [weekStart, setWeekStart] = useState<string | null>(null);

  useEffect(() => {
    if (!boundary) return;
    setWeekStart((current) => current ?? (page === 'plan' ? boundary.planningWeek : boundary.thisWeek));
  }, [boundary, page]);

  const load = useCallback(async () => {
    try {
      setError(null);
      if (!weekStart) return;
      const [todayView, weekView] = await Promise.all([
        api.today(language),
        api.week(weekStart, language),
      ]);
      setToday(todayView);
      setWeek(weekView);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not load the week');
    } finally {
      setLoading(false);
    }
  }, [language, weekStart]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading || !weekStart) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50vh' }}>
        <Spinner />
      </div>
    );
  }

  if (error) return <p className="banner warn">{error}</p>;

  const props: DashboardModuleProps = {
    today,
    week,
    language,
    refresh: () => void load(),
    isDevice: me.actor?.kind === 'device',
  };

  const nav = NAV[language];
  const isCurrentWeek = weekStart === boundary?.thisWeek;

  return (
    <div className="stack">
      <div className="row" style={{ gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <h1 className="page-title" style={{ flex: 1 }}>
          {TITLES[page][language]}
        </h1>

        {/* Which week this is, and a way to move — the planner used to open on
            a week that was mostly over with nothing saying so. */}
        {page === 'plan' && (
          <div className="row week-nav" style={{ gap: 6 }}>
            <button
              className="btn small"
              aria-label={nav.prev}
              onClick={() => setWeekStart((w) => (w ? addDays(w, -7) : w))}
            >
              ‹
            </button>
            <span className="week-range">
              {dayName(weekStart, language).slice(0, 3)} {shortDate(weekStart, language)} –{' '}
              {shortDate(addDays(weekStart, 6), language)}
              {isCurrentWeek && <span className="dim"> · {nav.current}</span>}
            </span>
            <button
              className="btn small"
              aria-label={nav.next}
              onClick={() => setWeekStart((w) => (w ? addDays(w, 7) : w))}
            >
              ›
            </button>
            {!isCurrentWeek && (
              <button className="btn small" onClick={() => boundary && setWeekStart(boundary.thisWeek)}>
                {nav.thisWeek}
              </button>
            )}
          </div>
        )}
      </div>
      {visibleModules(page, props).map((module) => (
        <module.component key={module.id} {...props} />
      ))}
    </div>
  );
}
